import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Grid,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { pagesApi } from '@api/pages';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { useCan } from '@shared/rbac/useCan';
import { useAuth } from '@features/auth/hooks/useAuth';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import {
  PAGE_FILTER_STATUSES,
  PAGE_TYPES,
  toContentStatus,
  usePageStatusLabel,
  usePageTypeLabel,
  type PageListItem,
  type PageStatus,
  type PageType,
} from '../types';

type StatusFilter = PageStatus | 'ALL';
type TypeFilter = PageType | 'ALL';

/**
 * S7 — Pages list page wired to the real backend.
 *
 * Workflow buttons (publish/approve/archive) are gated on
 * `user.role === 'ADMIN'` per the Foundation guidance (BE policy namespace
 * `content.publish` is admin-only; the FE permission `PAGE_PUBLISH` is the
 * intent layer above it).
 */
export function PageListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation(['pages', 'common']);
  const statusLabel = usePageStatusLabel();
  const typeLabel = usePageTypeLabel();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const canCreate = useCan(PERMISSIONS.PAGE_CREATE);
  const canDelete = useCan(PERMISSIONS.PAGE_DELETE);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [pageType, setPageType] = useState<TypeFilter>('ALL');
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [languageId, setLanguageId] = useState<string | undefined>();

  const { data, isFetching } = useQuery({
    queryKey: [
      'pages',
      {
        page,
        pageSize,
        search: debouncedSearch,
        status,
        pageType,
        publishedOnly,
        languageId,
      },
    ],
    queryFn: () =>
      pagesApi.list({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        status: status === 'ALL' ? undefined : status,
        pageType: pageType === 'ALL' ? undefined : pageType,
        publishedOnly: publishedOnly || undefined,
        languageId,
        sortBy: 'updatedAt',
      }),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (p: PageListItem) => pagesApi.remove(p.id),
    onSuccess: async (_d, p) => {
      toast.success(t('toast.deleted', { title: p.title }));
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.delete',
        resource: 'page',
        resourceId: p.id,
        summary: t('audit.deleted', { title: p.title }),
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        key: 'title',
        title: t('table.columns.title'),
        dataIndex: 'title',
        render: (_: unknown, row: PageListItem) => (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontWeight: 600 }}>{row.title}</div>
            <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
              /{row.slug}
            </div>
          </div>
        ),
      },
      {
        key: 'pageType',
        title: t('table.columns.type'),
        dataIndex: 'pageType',
        width: 140,
        render: (pt: PageType) => <Tag>{typeLabel(pt)}</Tag>,
      },
      {
        key: 'status',
        title: t('table.columns.status'),
        dataIndex: 'status',
        width: 150,
        render: (s: PageStatus) => (
          <Tooltip title={statusLabel(s)}>
            <span>
              <StatusBadge kind="content" status={toContentStatus(s)} />
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'sortOrder',
        title: t('table.columns.sortOrder'),
        dataIndex: 'sortOrder',
        width: 70,
        align: 'right' as const,
        render: (n: number) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</span>
        ),
      },
      {
        key: 'updatedAt',
        title: t('table.columns.updatedAt'),
        dataIndex: 'updatedAt',
        width: 130,
        render: (v: string) => (
          <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
            {dayjs(v).format('DD.MM.YYYY')}
          </span>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 110,
        render: (_: unknown, row: PageListItem) => (
          <Space>
            <Tooltip title={t('tooltip.edit')}>
              <Button
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => {
                  void navigate(ROUTES.PAGE_EDIT.replace(':id', row.id));
                }}
              />
            </Tooltip>
            {canDelete || isAdmin ? (
              <Popconfirm
                title={t('delete.title')}
                description={
                  row.status === 'PUBLISHED'
                    ? t('delete.publishedDescription', { title: row.title })
                    : t('delete.confirmDescription', { title: row.title })
                }
                okText={t('delete.ok')}
                okButtonProps={{ danger: true }}
                cancelText={t('delete.cancel')}
                onConfirm={() => deleteMutation.mutate(row)}
              >
                <Tooltip title={t('tooltip.delete')}>
                  <Button size="small" danger icon={<Trash2 size={14} />} />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        ),
      },
    ],
    [navigate, canDelete, isAdmin, deleteMutation, t, statusLabel, typeLabel],
  );

  return (
    <div>
      <PageHeader
        title={t('list.title')}
        description={t('list.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('list.breadcrumbSelf') },
        ]}
        actions={
          <Space wrap>
            {canCreate ? (
              <Button
                type="primary"
                icon={<Plus size={14} />}
                onClick={() => {
                  void navigate(ROUTES.PAGE_NEW);
                }}
              >
                {t('list.newPage')}
              </Button>
            ) : null}
          </Space>
        }
      />

      <Card>
        <DataTable<PageListItem>
          data={items}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          exportable
          exportFileName="pages"
          search={{
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: t('list.searchPlaceholder'),
          }}
          filters={
            <Space wrap size={isMobile ? 4 : 8}>
              <Segmented<StatusFilter>
                size="small"
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
                options={[
                  { value: 'ALL', label: t('common:labels.all') },
                  ...PAGE_FILTER_STATUSES.map((s) => ({
                    value: s,
                    label: statusLabel(s),
                  })),
                ]}
              />
              <Select<TypeFilter>
                size="small"
                value={pageType}
                onChange={(v) => {
                  setPageType(v);
                  setPage(1);
                }}
                style={{ minWidth: isMobile ? 140 : 180 }}
                options={[
                  { value: 'ALL', label: t('list.filters.allTypes') },
                  ...PAGE_TYPES.map((pt) => ({
                    value: pt,
                    label: typeLabel(pt),
                  })),
                ]}
              />
              <LanguagePicker
                value={languageId}
                onChange={(v) => {
                  setLanguageId(v);
                  setPage(1);
                }}
                allowClear
                size="small"
                placeholder={t('common:contentLanguage.filterAll')}
                style={{ minWidth: isMobile ? 140 : 160 }}
              />
              <Space size={6}>
                <Switch
                  size="small"
                  checked={publishedOnly}
                  onChange={(v) => {
                    setPublishedOnly(v);
                    setPage(1);
                  }}
                />
                <span style={{ fontSize: 12 }}>
                  {t('list.filters.publishedOnly')}
                </span>
              </Space>
            </Space>
          }
          pagination={{
            page,
            size: pageSize,
            total,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>
    </div>
  );
}
